import type { Node, Edge } from '@xyflow/react';
import { IOType } from '@erp/shared-types/node-graph';
import type { WorkflowDefinitionResponse } from '@erp/rpc';

export interface MMGraphSocket {
  id: string;
  ioType: IOType;
  socketType: string; // e.g. 'string', 'entity_reference', 'json', etc.
}

export interface NodeDataType extends Record<string, unknown> {
  label: string;
  inputs: {
    [key: string]: MMGraphSocket;
  };
  outputs: {
    [key: string]: MMGraphSocket;
  };
  stepType?: string;
  category?: string;
  config?: Record<string, unknown>;
  emits?: string[];
}

/**
 * Convert a workflow definition to React Flow nodes and edges
 */
export function definitionToGraph(definition: WorkflowDefinitionResponse): {
  nodes: Node<NodeDataType>[];
  edges: Edge[];
} {
  // Create a map of step IDs to indices for edge mapping
  const stepIdToIndex = new Map<string, number>();
  definition.steps.forEach((step, index) => {
    stepIdToIndex.set(step.id, index);
  });

  // Convert definition steps to React Flow nodes
  const nodes: Node<NodeDataType>[] = definition.steps.map((step) => {
    // Convert sockets to the format expected by StepNode
    const inputs: { [key: string]: MMGraphSocket } = {};
    const outputs: { [key: string]: MMGraphSocket } = {};

    step.sockets.inputs.forEach((socket) => {
      inputs[socket.name] = {
        id: socket.id,
        ioType: IOType.INPUT,
        socketType: socket.type,
      };
    });

    step.sockets.outputs.forEach((socket) => {
      outputs[socket.name] = {
        id: socket.id,
        ioType: IOType.OUTPUT,
        socketType: socket.type,
      };
    });

    return {
      id: step.id,
      type: 'stepNode',
      position: step.position,
      data: {
        label: step.name,
        inputs,
        outputs,
        stepType: step.type,
        category: step.category,
        config: step.config,
      },
    };
  });

  // Convert definition edges to React Flow edges
  const edges: Edge[] = definition.edges
    .map((edge) => {
      const sourceIndex = stepIdToIndex.get(edge.sourceStepId);
      const targetIndex = stepIdToIndex.get(edge.targetStepId);

      if (sourceIndex === undefined || targetIndex === undefined) {
        // Skip edges with invalid step references
        return null;
      }

      return {
        id: edge.id, // Preserve original edge UUID
        source: edge.sourceStepId,
        sourceHandle: edge.sourceSocketId,
        target: edge.targetStepId,
        targetHandle: edge.targetSocketId,
        animated: true,
        type: 'deletable',
      } as Edge;
    })
    .filter((edge): edge is Edge => edge !== null);

  return { nodes, edges };
}
